import { AnyDefinition, DatabaseNode, InferType, toSchema } from "@lifeat/shared/data-models/definition";
import { db } from "@firebase-local/firebase";
import { logError } from "./ConsoleLoggerService";

type SubscriptionCallbackPayload<T> = { success: false; error: unknown } | { success: true; value: T };
type Unsubscribe = () => void;
type SubscriptionCallback<T> = (payload: SubscriptionCallbackPayload<T>) => void;
type EventType = "value" | "child_added" | "child_changed" | "child_moved" | "child_removed";

/**
 * Adapter service for Firebase Realtime Database.
 *
 * Prefer to use this service over directly using the firebase package.
 * The methods of this service are aware of the structure of LifeAt database
 * and returns correct type.
 */
export function DatabaseService() {
    return {
        /**
         * Read value from database node once.
         * @param node Database node to read.
         * @returns the node value or `null` if the node does not exist on database.
         */
        get: async <TDefinition extends AnyDefinition, TPath extends string>(
            node: DatabaseNode<TDefinition, TPath>,
        ): Promise<InferType<TDefinition> | null | undefined> => {
            const schema = toSchema(node.definition);
            const ref = db.ref(node.path);
            const snapshot = await ref.once("value");
            if (!snapshot.exists()) {
                return null;
            }
            const raw = snapshot.val();
            const parsed = schema.safeParse(raw);
            if (parsed.success === false) {
                logError(parsed.error);
                return raw;
            }
            return parsed.data;
        },
        /**
         * Write value to database node.
         * @param node Database node to set.
         * @param value Value to write.
         */
        set: async <TDefinition extends AnyDefinition, TPath extends string>(
            node: DatabaseNode<TDefinition, TPath>,
            value: InferType<TDefinition>,
        ) => {
            const ref = db.ref(node.path);
            await ref.set(value);
        },
        /**
         * Partially update a database node.
         * @param node Database node to update.
         * @param value Partial value to write.
         */
        update: async <TDefinition extends AnyDefinition, TPath extends string>(
            node: DatabaseNode<TDefinition, TPath>,
            value: Partial<InferType<TDefinition>>,
        ) => {
            const ref = db.ref(node.path);
            await ref.update(value);
        },
        /**
         * Listen to changes in the given database node.
         * @param node Database node to be observed.
         * @param type Event type.
         * @param callback Listener function. The new value from database node is passed as a parameter.
         * @returns Unsubscribe function. Callback function will not be called anymore after unsubscribe function is called.
         */
        subscribe: <TDefinition extends AnyDefinition, TPath extends string>(
            node: DatabaseNode<TDefinition, TPath>,
            type: EventType,
            callback: SubscriptionCallback<InferType<TDefinition> | null | undefined>,
        ): Unsubscribe => {
            const ref = db.ref(node.path);
            const schema = toSchema(node.definition);
            const adapter: Parameters<typeof ref.on>[1] = (snapshot) => {
                if (!snapshot.exists()) {
                    callback({ success: true, value: null });
                } else {
                    const raw = snapshot.val();
                    const parsed = schema.safeParse(raw);
                    if (parsed.success === false) {
                        logError(parsed.error);
                        callback({ success: true, value: raw });
                    } else {
                        callback({ success: true, value: parsed.data });
                    }
                }
            };
            ref.on(type, adapter);

            return () => ref.off(type, adapter);
        },
    };
}

export type DatabaseService = ReturnType<typeof DatabaseService>;
